import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { 
  ArrowLeft, 
  User,
  Key,
  FloppyDisk,
  Eye,
  EyeSlash
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LOGO_1 = "https://customer-assets.emergentagent.com/job_medform-standby/artifacts/3xn9wu25_standby_transparent.png";
const LOGO_2 = "https://customer-assets.emergentagent.com/job_medform-standby/artifacts/v4idzbss_on_site_transparent.png";

export default function ProfilePage() {
  const { token, user, changePassword } = useAuth();
  const [loading, setLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false);
  
  const [profile, setProfile] = useState({
    first_name: '',
    last_name: '',
    email: '',
    emt_number: '',
    role: ''
  });

  const [passwords, setPasswords] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const fetchProfile = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/profile`, {});
      setProfile(data);
    } catch (error) {
      toast.error('Failed to load profile');
    }
  }, [token]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.put(`${API_URL}/api/profile`, {
        first_name: profile.first_name,
        last_name: profile.last_name,
        emt_number: profile.emt_number
      }, {});
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwords.new_password !== passwords.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwords.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setPasswordLoading(true);
    
    const result = await changePassword(passwords.current_password, passwords.new_password);
    
    setPasswordLoading(false);
    
    if (result.success) {
      toast.success('Password changed successfully');
      setPasswords({ current_password: '', new_password: '', confirm_password: '' });
    } else {
      toast.error(result.error);
    }
  };

  const getRoleBadgeClass = (role) => {
    switch(role) {
      case 'admin': return 'bg-purple-100 text-purple-700';
      case 'provider': return 'bg-blue-100 text-blue-700';
      case 'qa_qi': return 'bg-green-100 text-green-700';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  const getRoleLabel = (role) => {
    if (role === 'qa_qi') return 'QA/QI';
    return role?.toUpperCase() || 'USER';
  };

  return (
    <div className="min-h-screen bg-slate-100" data-testid="profile-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" data-testid="back-to-dashboard">
                <Button variant="ghost" size="sm" className="rounded-sm">
                  <ArrowLeft size={18} className="mr-2" />
                  Dashboard
                </Button>
              </Link>
              <div className="h-6 w-px bg-slate-200" />
              <div className="flex items-center gap-3">
                <img src={LOGO_1} alt="Logo 1" className="h-8 object-contain" />
                <img src={LOGO_2} alt="Logo 2" className="h-8 object-contain" />
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900">My Profile</h1>
          <p className="text-slate-500 text-sm mt-1">Manage your account settings</p>
        </div>

        <div className="space-y-6">
          {/* Profile Information */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User size={20} className="text-blue-600" />
                Profile Information
              </CardTitle>
              <CardDescription>Update your personal details</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleUpdateProfile} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                      First Name
                    </Label>
                    <Input
                      value={profile.first_name}
                      onChange={(e) => setProfile({...profile, first_name: e.target.value})}
                      className="rounded-sm"
                      data-testid="profile-firstname-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Last Name
                    </Label>
                    <Input
                      value={profile.last_name}
                      onChange={(e) => setProfile({...profile, last_name: e.target.value})}
                      className="rounded-sm"
                      data-testid="profile-lastname-input"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Email/Username
                  </Label>
                  <Input
                    value={profile.email}
                    disabled
                    className="rounded-sm bg-slate-50"
                    data-testid="profile-email-input"
                  />
                  <p className="text-xs text-slate-400">Email cannot be changed</p>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                      EMT/Paramedic Number
                    </Label>
                    <Input
                      value={profile.emt_number || ''}
                      onChange={(e) => setProfile({...profile, emt_number: e.target.value})}
                      placeholder="NYS EMT/Paramedic #"
                      className="rounded-sm"
                      data-testid="profile-emt-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Account Type
                    </Label>
                    <div className="pt-2">
                      <span className={`px-3 py-1.5 text-sm font-medium rounded ${getRoleBadgeClass(profile.role)}`}>
                        {getRoleLabel(profile.role)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="pt-4">
                  <Button 
                    type="submit" 
                    className="bg-slate-900 hover:bg-slate-800 rounded-sm"
                    disabled={loading}
                    data-testid="save-profile-button"
                  >
                    <FloppyDisk size={18} className="mr-2" />
                    {loading ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Change Password */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Key size={20} className="text-orange-600" />
                Change Password
              </CardTitle>
              <CardDescription>Update your password for security</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    Current Password
                  </Label>
                  <div className="relative">
                    <Input
                      type={showPasswords ? 'text' : 'password'}
                      value={passwords.current_password}
                      onChange={(e) => setPasswords({...passwords, current_password: e.target.value})}
                      className="rounded-sm pr-10"
                      required
                      data-testid="current-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords(!showPasswords)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPasswords ? <EyeSlash size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                      New Password
                    </Label>
                    <Input
                      type={showPasswords ? 'text' : 'password'}
                      value={passwords.new_password}
                      onChange={(e) => setPasswords({...passwords, new_password: e.target.value})}
                      className="rounded-sm"
                      required
                      data-testid="new-password-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                      Confirm New Password
                    </Label>
                    <Input
                      type={showPasswords ? 'text' : 'password'}
                      value={passwords.confirm_password}
                      onChange={(e) => setPasswords({...passwords, confirm_password: e.target.value})}
                      className="rounded-sm"
                      required
                      data-testid="confirm-password-input"
                    />
                  </div>
                </div>

                <div className="pt-4">
                  <Button 
                    type="submit" 
                    variant="outline"
                    className="rounded-sm border-orange-300 text-orange-700 hover:bg-orange-50"
                    disabled={passwordLoading}
                    data-testid="change-password-button"
                  >
                    <Key size={18} className="mr-2" />
                    {passwordLoading ? 'Changing...' : 'Change Password'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
